import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { cryptoEngine } from "../crypto/index.js";
import { MAX_ATTACHMENT_BYTES } from "@truewave/protocol";
import { clearConversation, deleteContact, getAttachmentBlob, type Message, type ReplyRef } from "../storage/db.js";
import { messaging } from "../services/messaging.js";
import { timeLeft } from "../storage/sweeper.js";
import { useCallStore } from "../state/call.js";
import { describePresence, usePresence } from "../state/presence.js";
import { useContacts, useMessages, useTick } from "../hooks.js";
import { MessageActions } from "./MessageActions.js";
import { VoiceBubble, fmtDuration } from "./VoiceBubble.js";
import {
  MAX_VOICE_MS,
  MIN_VOICE_MS,
  startVoiceRecording,
  voiceFileExtension,
  voiceRecordingSupported,
  type VoiceRecorderHandle,
  type VoiceRecording,
} from "../calling/recorder.js";
import { AttachmentViewer, useViewer } from "./AttachmentViewer.js";
import { isBlurModeOn, isRevealLatched, resumeAutoLock, suspendAutoLock } from "../privacy.js";

function fmtLeft(ms: number | null): string {
  if (ms === null) return "sending…";
  if (ms <= 0) return "gone";
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return `${m}:${s.toString().padStart(2, "0")} left`;
}

/** WhatsApp-style ticks for an outgoing message. */
function StatusTicks({ status }: { status: Message["status"] }) {
  switch (status) {
    case "queued":
      return <span className="ticks" title="Waiting to send" aria-label="Waiting to send">🕓</span>;
    case "sent":
      return <span className="ticks" title="Sent" aria-label="Sent">✓</span>;
    case "delivered":
      return <span className="ticks" title="Delivered" aria-label="Delivered">✓✓</span>;
    case "read":
      return <span className="ticks read" title="Read" aria-label="Read">✓✓</span>;
    default:
      return <span className="ticks" title="Not sent" aria-label="Not sent">!</span>;
  }
}

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function AttachmentBubble({ m }: { m: Message }) {
  const info = m.attachment;
  if (info?.voice) return <VoiceBubble m={m} />;
  if (!info || info.viewed) {
    return <div className="attachment-card gone">📎 Photo/file · opened</div>;
  }
  const mimeType = info.mimeType;
  const filename = info.filename;
  async function open() {
    // Received: read it first and only then consume it (view-once), so a
    // failure can never burn the one view. Shown in TrueWave's own viewer —
    // an iPhone Home Screen app can't open a blob: URL in a new window.
    const bytes = await getAttachmentBlob(m.id);
    if (!bytes) return;
    useViewer.getState().show(bytes, mimeType, filename);
    if (m.dir === "in") await messaging.viewAttachment(m.id, m.peerUserId);
  }
  return (
    <button className="attachment-card" onClick={() => void open()}>
      📎 {info.filename || "Attachment"}
      <span className="sub">{fmtSize(info.size)} · tap to view{m.dir === "in" ? " (once)" : ""}</span>
    </button>
  );
}

// http(s)://… or www.… up to the next whitespace; trailing punctuation that
// usually ends a sentence rather than the link is trimmed off below.
const LINK_RE = /(?<![\w@.\/])(?:https?:\/\/|www\.)[^\s<>"']+/gi;
const TRAILING = /[.,!?;:)\]}'"»]+$/;

/**
 * Message text with its links tappable. They open outside TrueWave, so the
 * phone can hand them to the right app: an Instagram, YouTube or WhatsApp
 * link opens in that app when it's installed, anything else in the browser.
 * Only http(s) links are ever made clickable — never javascript:, file: etc.
 */
function Linkified({ text }: { text: string }) {
  const parts: React.ReactNode[] = [];
  let last = 0;
  for (const match of text.matchAll(LINK_RE)) {
    const start = match.index ?? 0;
    let raw = match[0];
    const trail = raw.match(TRAILING)?.[0] ?? "";
    if (trail) raw = raw.slice(0, raw.length - trail.length);
    if (!raw) continue;
    const href = raw.toLowerCase().startsWith("www.") ? `https://${raw}` : raw;
    if (start > last) parts.push(text.slice(last, start));
    parts.push(
      <a key={start} className="msg-link" href={href} target="_blank" rel="noopener noreferrer">
        {raw}
      </a>,
    );
    last = start + raw.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

/** Short text standing in for a message inside a reply quote. */
function snippetOf(m: Message): string {
  if (m.attachment?.voice) return "🎤 Voice message";
  if (m.kind === "attachment") return `📎 ${m.attachment?.filename || "Attachment"}`;
  const firstLine = m.text.split("\n")[0] ?? "";
  return firstLine.length > 120 ? `${firstLine.slice(0, 120)}…` : firstLine;
}

/**
 * Long-press (touch) or right-click (mouse) to open a message's options. The
 * click that ends a long press is swallowed, so it doesn't also open an
 * attachment underneath. With "blur until touched" on, while the chat is still
 * blurred a hold is how you unblur it, so options take a double-tap then;
 * once unblurred, long-press works again.
 */
function useLongPress(onLongPress: () => void, ms = 450) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const origin = useRef<{ x: number; y: number } | null>(null);
  const fired = useRef(false);
  const lastTap = useRef<{ at: number; x: number; y: number } | null>(null);
  const cancel = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  return {
    onPointerDown(e: React.PointerEvent) {
      if (e.pointerType === "mouse") return; // mouse users right-click instead
      fired.current = false;
      origin.current = { x: e.clientX, y: e.clientY };
      cancel();
      if (isBlurModeOn() && !isRevealLatched()) return; // this hold unblurs; see onPointerUp for double-tap
      timer.current = setTimeout(() => {
        timer.current = null;
        fired.current = true;
        navigator.vibrate?.(15);
        onLongPress();
      }, ms);
    },
    onPointerMove(e: React.PointerEvent) {
      const o = origin.current;
      if (o && Math.hypot(e.clientX - o.x, e.clientY - o.y) > 10) cancel(); // scrolling, not pressing
    },
    onPointerUp(e: React.PointerEvent) {
      cancel();
      if (e.pointerType === "mouse" || !isBlurModeOn()) return;
      const prev = lastTap.current;
      const now = Date.now();
      if (prev && now - prev.at < 350 && Math.hypot(e.clientX - prev.x, e.clientY - prev.y) < 30) {
        lastTap.current = null;
        fired.current = true;
        navigator.vibrate?.(15);
        onLongPress();
      } else {
        lastTap.current = { at: now, x: e.clientX, y: e.clientY };
      }
    },
    onPointerCancel: cancel,
    onPointerLeave: cancel,
    onContextMenu(e: React.MouseEvent) {
      e.preventDefault();
      cancel();
      onLongPress();
    },
    onClickCapture(e: React.MouseEvent) {
      if (fired.current) {
        fired.current = false;
        e.preventDefault();
        e.stopPropagation();
      }
    },
  };
}

interface BubbleProps {
  m: Message;
  now: number;
  quoteAuthor: (authorId: string) => string;
  /** The quoted original was deleted for everyone: don't keep showing its text. */
  quoteDeleted: boolean;
  onOpenActions(m: Message): void;
  onReact(m: Message, emoji: string | null): void;
}

function MessageBubble({ m, now, quoteAuthor, quoteDeleted, onOpenActions, onReact }: BubbleProps) {
  const press = useLongPress(() => onOpenActions(m));
  const out = m.dir === "out";
  const reactions = [m.reactions?.theirs, m.reactions?.mine].filter((r): r is string => Boolean(r));

  function jumpTo(id: string) {
    const el = document.getElementById(`msg-${id}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("flash");
    setTimeout(() => el.classList.remove("flash"), 1200);
  }

  return (
    <div id={`msg-${m.id}`} className={`bubble-row ${out ? "out" : ""} ${reactions.length ? "has-reactions" : ""}`}>
      <div className={`bubble ${out ? "out" : ""} ${m.deleted ? "deleted" : ""}`} {...press}>
        <button className="bubble-menu" aria-label="Message options" onClick={() => onOpenActions(m)}>
          ⌄
        </button>
        {m.replyTo && !m.deleted && (
          <button className="quote" onClick={() => jumpTo(m.replyTo!.id)}>
            <strong>{quoteAuthor(m.replyTo.authorId)}</strong>
            <span>{quoteDeleted ? "🚫 This message was deleted" : m.replyTo.snippet}</span>
          </button>
        )}
        {m.deleted ? (
          <div className="tombstone">🚫 {out ? "You deleted this message" : "This message was deleted"}</div>
        ) : m.kind === "attachment" ? (
          <AttachmentBubble m={m} />
        ) : (
          <div className="bubble-text">
            <Linkified text={m.text} />
          </div>
        )}
        <div className="meta">
          {m.edited && !m.deleted && <span>edited</span>}
          <span>{new Date(m.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
          {m.expiresAt !== null && (
            <>
              <span>·</span>
              <span>{fmtLeft(timeLeft(m.expiresAt, now))}</span>
            </>
          )}
          {out && !m.deleted && <StatusTicks status={m.status} />}
        </div>
      </div>
      {reactions.length > 0 && !m.deleted && (
        <button
          className="reactions"
          aria-label={m.reactions?.mine ? "Remove your reaction" : "Reactions"}
          onClick={() => (m.reactions?.mine ? onReact(m, null) : onOpenActions(m))}
        >
          {reactions.join(" ")}
        </button>
      )}
    </div>
  );
}

export function Chat() {
  const { peerUserId } = useParams();
  const navigate = useNavigate();
  const messages = useMessages(peerUserId);
  const now = useTick();
  // Live, so a contact's real username replaces the placeholder id as soon
  // as it's looked up.
  const contact = useContacts().find((c) => c.peerUserId === peerUserId);
  const presence = describePresence(usePresence(peerUserId), now);
  const [draft, setDraft] = useState("");
  const [safety, setSafety] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  /** Message whose options sheet is open. */
  const [actionsFor, setActionsFor] = useState<Message | null>(null);
  const [replyingTo, setReplyingTo] = useState<Message | null>(null);
  const [editing, setEditing] = useState<Message | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [recorder, setRecorder] = useState<VoiceRecorderHandle | null>(null);
  const [recordedMs, setRecordedMs] = useState(0);
  const callPhase = useCallStore((s) => s.phase);
  const inputRef = useRef<HTMLInputElement>(null);
  const myUserId = messaging.account?.userId;
  const endRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  // Clear this conversation's contribution to the app-icon badge whenever the
  // user is actually looking at it — on open, when a new message arrives while
  // it's open, and when the tab regains focus while still parked here. Guarded
  // by visibility so a backgrounded tab doesn't silently mark things read.
  useEffect(() => {
    if (!peerUserId) return;
    const markIfVisible = () => {
      if (document.visibilityState === "visible") void messaging.markRead(peerUserId);
    };
    markIfVisible();
    document.addEventListener("visibilitychange", markIfVisible);
    return () => document.removeEventListener("visibilitychange", markIfVisible);
  }, [peerUserId, messages.length]);

  const ordered = useMemo(() => [...messages].sort((a, b) => a.createdAt - b.createdAt), [messages]);
  const deletedIds = useMemo(() => new Set(messages.filter((m) => m.deleted).map((m) => m.id)), [messages]);

  function flash(text: string) {
    setToast(text);
    setTimeout(() => setToast((t) => (t === text ? null : t)), 1800);
  }

  function quoteAuthor(authorId: string): string {
    if (authorId === myUserId) return "You";
    return contact?.username ?? "Them";
  }

  async function send() {
    const text = draft.trim();
    if (!text || !peerUserId) return;
    setDraft("");
    if (editing) {
      const target = editing;
      setEditing(null);
      await messaging.editMessage(peerUserId, target.id, text);
      return;
    }
    const reply: ReplyRef | undefined =
      replyingTo && myUserId
        ? {
            id: replyingTo.id,
            snippet: snippetOf(replyingTo),
            authorId: replyingTo.dir === "out" ? myUserId : replyingTo.peerUserId,
          }
        : undefined;
    setReplyingTo(null);
    await messaging.sendText(peerUserId, text, reply);
  }

  function cancelComposerMode() {
    if (editing) setDraft("");
    setEditing(null);
    setReplyingTo(null);
  }

  const actions = actionsFor && {
    onClose: () => setActionsFor(null),
    onReply: () => {
      setEditing(null);
      setReplyingTo(actionsFor);
      setActionsFor(null);
      inputRef.current?.focus();
    },
    onCopy: () => {
      void navigator.clipboard?.writeText(actionsFor.text).then(
        () => flash("Copied"),
        () => flash("Couldn't copy"),
      );
      setActionsFor(null);
    },
    onEdit: () => {
      setReplyingTo(null);
      setEditing(actionsFor);
      setDraft(actionsFor.text);
      setActionsFor(null);
      inputRef.current?.focus();
    },
    onReact: (emoji: string | null) => {
      if (peerUserId) void messaging.react(peerUserId, actionsFor.id, emoji);
      setActionsFor(null);
    },
    onDeleteForMe: () => {
      void messaging.deleteForMe(actionsFor.id);
      if (editing?.id === actionsFor.id || replyingTo?.id === actionsFor.id) cancelComposerMode();
      setActionsFor(null);
    },
    onDeleteForEveryone: () => {
      if (peerUserId) void messaging.deleteForEveryone(peerUserId, actionsFor.id);
      if (editing?.id === actionsFor.id || replyingTo?.id === actionsFor.id) cancelComposerMode();
      setActionsFor(null);
    },
  };

  async function sendAttachmentFile(file: File, meta?: { voice?: true; durationMs?: number }) {
    if (!peerUserId) return;
    if (file.size > MAX_ATTACHMENT_BYTES) {
      flash("That file is too big (25 MB max)");
      return;
    }
    try {
      await messaging.sendAttachment(peerUserId, file, meta);
    } catch (err) {
      console.error("sending an attachment failed", err);
      flash("Couldn't send that");
    }
  }

  async function sendFile(e: React.ChangeEvent<HTMLInputElement>) {
    resumeAutoLock();
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    await sendAttachmentFile(file);
  }

  function voiceFile(recording: VoiceRecording): File {
    const stamp = new Date().toTimeString().slice(0, 5).replace(":", "-");
    return new File([recording.blob], `voice-${stamp}.${voiceFileExtension(recording.mimeType)}`, {
      type: recording.mimeType,
    });
  }

  async function deliverRecording(recording: VoiceRecording) {
    setRecorder(null);
    setRecordedMs(0);
    resumeAutoLock();
    if (recording.durationMs < MIN_VOICE_MS) {
      flash("Too short — hold on a moment longer");
      return;
    }
    await sendAttachmentFile(voiceFile(recording), { voice: true, durationMs: recording.durationMs });
  }

  async function startRecording() {
    if (recorder || callPhase !== "idle") return;
    // The permission sheet takes the app off screen on iOS, and a long take
    // shouldn't trip the auto-lock either.
    suspendAutoLock(MAX_VOICE_MS + 15_000);
    try {
      const handle = await startVoiceRecording((auto) => void deliverRecording(auto));
      setRecorder(handle);
      setRecordedMs(0);
    } catch (err) {
      resumeAutoLock();
      const name = (err as { name?: string }).name;
      if (name === "NotAllowedError") flash("Microphone access is off for TrueWave");
      else if (name === "NotFoundError") flash("No microphone found");
      else flash("Couldn't start recording");
    }
  }

  async function finishRecording() {
    if (!recorder) return;
    try {
      await deliverRecording(await recorder.stop());
    } catch (err) {
      console.error("finishing the recording failed", err);
      setRecorder(null);
      resumeAutoLock();
      flash("Couldn't finish that recording");
    }
  }

  function cancelRecording() {
    recorder?.cancel();
    setRecorder(null);
    setRecordedMs(0);
    resumeAutoLock();
    flash("Recording discarded");
  }

  async function showSafety() {
    if (!contact) return;
    setMenuOpen(false);
    const sn = await cryptoEngine.safetyNumber(contact.conversationId);
    setSafety(sn.digits.replace(/(\d{5})/g, "$1 ").trim());
  }

  async function clearChat() {
    setMenuOpen(false);
    if (!peerUserId) return;
    if (!confirm("Permanently clear this chat and its call log? This can't be undone.")) return;
    await clearConversation(peerUserId);
  }

  async function removeContact() {
    setMenuOpen(false);
    if (!peerUserId) return;
    if (!confirm("Delete this contact and everything exchanged with them? This can't be undone.")) return;
    await deleteContact(peerUserId);
    navigate("/");
  }

  const startCall = useCallStore((s) => s.startCall);

  // Tick the recording timer.
  useEffect(() => {
    if (!recorder) return;
    const t = setInterval(() => setRecordedMs(recorder.elapsedMs()), 200);
    return () => clearInterval(t);
  }, [recorder]);

  function call(video: boolean) {
    if (!peerUserId) return;
    void startCall(peerUserId, contact?.username ?? peerUserId.slice(0, 8), video);
  }

  return (
    <div className="chat">
      <div className="topbar">
        <Link to="/"><button className="topbar-icon-btn" title="Back" aria-label="Back">←</button></Link>
        <div className="grow topbar-title sensitive">
          <strong>{contact?.username ?? peerUserId?.slice(0, 8)}</strong>
          {presence && <span className={`topbar-sub ${presence === "online" ? "online" : ""}`}>{presence}</span>}
        </div>
        <button className="topbar-icon-btn" onClick={() => call(false)} title="Call" aria-label="Call">📞</button>
        <button className="topbar-icon-btn" onClick={() => call(true)} title="Video call" aria-label="Video call">🎥</button>
        <div className="menu-wrap">
          <button
            className="topbar-icon-btn"
            onClick={() => setMenuOpen((v) => !v)}
            title="More"
            aria-label="More options"
          >
            ⋮
          </button>
          {menuOpen && (
            <>
              <button className="menu-backdrop" aria-label="Close menu" onClick={() => setMenuOpen(false)} />
              <div className="menu" role="menu">
                <button role="menuitem" onClick={() => void showSafety()}>
                  <span className="menu-icon">🛡️</span> Verify safety number
                </button>
                <button role="menuitem" onClick={() => void clearChat()}>
                  <span className="menu-icon">🧹</span> Clear chat
                </button>
                <button role="menuitem" className="danger" onClick={() => void removeContact()}>
                  <span className="menu-icon">🗑️</span> Delete contact
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {safety && (
        <div className="banner" style={{ margin: 16 }}>
          <div>Compare these numbers with your contact out loud or in person. If they match, the conversation is not being intercepted.</div>
          <div className="safety">{safety}</div>
        </div>
      )}

      <div className="hold-hint" aria-hidden>✋ Touch &amp; hold to read</div>
      <div className="messages sensitive">
        {ordered.map((m) => (
          <MessageBubble
            key={m.id}
            m={m}
            now={now}
            quoteAuthor={quoteAuthor}
            quoteDeleted={Boolean(m.replyTo && deletedIds.has(m.replyTo.id))}
            onOpenActions={setActionsFor}
            onReact={(msg, emoji) => peerUserId && void messaging.react(peerUserId, msg.id, emoji)}
          />
        ))}
        <div ref={endRef} />
      </div>

      {toast && <div className="toast" role="status">{toast}</div>}

      {(replyingTo || editing) && (
        <div className="composer-context">
          <div className="composer-context-text sensitive">
            <strong>{editing ? "Edit message" : `Replying to ${quoteAuthor(replyingTo!.dir === "out" ? myUserId ?? "" : replyingTo!.peerUserId)}`}</strong>
            <span>{snippetOf((editing ?? replyingTo)!)}</span>
          </div>
          <button className="ghost" aria-label={editing ? "Cancel editing" : "Cancel reply"} onClick={cancelComposerMode}>
            ✕
          </button>
        </div>
      )}

      {recorder && (
        <div className="recorder-bar">
          <span className="recorder-dot" aria-hidden />
          <span className="recorder-time">{fmtDuration(recordedMs)}</span>
          <span className="recorder-note">Mic is on only while recording</span>
          <button className="ghost" onClick={cancelRecording} aria-label="Discard recording">
            🗑
          </button>
          <button className="primary" onClick={() => void finishRecording()} aria-label="Send voice message">
            ➤
          </button>
        </div>
      )}

      <div className="composer">
        <input
          ref={fileInputRef}
          type="file"
          hidden
          onChange={(e) => void sendFile(e)}
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*,video/*"
          capture="environment"
          hidden
          onChange={(e) => void sendFile(e)}
        />
        <button className="composer-btn ghost" onClick={() => {
            // The phone's picker takes the app off screen — don't auto-lock and lose the file.
            suspendAutoLock();
            fileInputRef.current?.click();
          }} title="Attach a file" aria-label="Attach a file">📎</button>
        <button className="composer-btn ghost" onClick={() => {
            suspendAutoLock();
            cameraInputRef.current?.click();
          }} title="Camera" aria-label="Camera">📷</button>
        {voiceRecordingSupported() && !editing && (
          <button
            className={`composer-btn ghost ${recorder ? "recording" : ""}`}
            onClick={() => (recorder ? void finishRecording() : void startRecording())}
            disabled={callPhase !== "idle"}
            title={recorder ? "Stop and send" : "Record a voice message"}
            aria-label={recorder ? "Stop and send voice message" : "Record a voice message"}
          >
            {recorder ? "⏹" : "🎤"}
          </button>
        )}
        <input
          ref={inputRef}
          className="composer-input"
          value={draft}
          placeholder={editing ? "Edit message" : "Message"}
          enterKeyHint="send"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void send();
            } else if (e.key === "Escape") {
              cancelComposerMode();
            }
          }}
        />
        <button
          className="composer-btn composer-send primary"
          onClick={() => void send()}
          disabled={!draft.trim()}
          title="Send"
          aria-label="Send"
        >
          {editing ? (
            "✓"
          ) : (
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden>
              <path d="M3.4 20.4 21 12 3.4 3.6l-.01 6.53L15 12 3.39 13.87z" />
            </svg>
          )}
        </button>
      </div>

      {actionsFor && actions && <MessageActions message={actionsFor} {...actions} />}
      <AttachmentViewer />
    </div>
  );
}
